/* eslint-disable camelcase */
import { createAction } from '@reduxjs/toolkit';
import * as Types from '../types';
import type * as CartTypes from '@/pages/winkelwagen/types';

/** Example:
const dispatch = useAppDispatch();

const purchaseAction = gmtPurchaseAction({
  orderId: checkoutResponse.orderNr,
  cartItems: [] // useAppSelector(state => selectLines(state)),
  costs: ...  // useAppSelector(state => selectCosts(state)),
});

dispatch(purchaseAction())
*/
interface IPurchaceEvent {
  orderId: string;
  cartItems: CartTypes.Product[];
  costs?: CartTypes.SummaryTotals;
  isNewUser?: boolean;
  email?: string;
}

interface IAddShippingInfoEvent {
  currency: string;
  shippingTier?: string;
  value: number;
  cartItems: CartTypes.Product[];
}

interface IAddPaymentInfoEvent {
  currency?: string;
  paymentType: string; // From MSP. issuer.name
  value: number;
  cartItems: CartTypes.Product[];
}

interface IViewItemEvent {
  currency: string;
  value: number;
  cartItem: CartTypes.Product;
}

interface IViewItemListEvent {
  itemListId: string;
  itemListName: string;
  cartItems: CartTypes.Product[];
}

// https://developers.google.com/analytics/devguides/collection/ga4/ecommerce?client_type=gtm#purchase-gtm
export const gmtPurchaseAction = createAction(
  `gmt/${Types.GTMEventTypes.PURCHASE}`,
  function prepare({ orderId, cartItems, costs, isNewUser = undefined, email }: IPurchaceEvent) {
    const subTotal = cartItems?.reduce((accumulator: number, current) => {
      return accumulator + current?.costs?.lineAmount?.exTax?.numeric ?? 0;
    }, 0);

    const tax = costs?.vatAmount?.numeric ?? 0;
    const shipment = costs?.shipment?.exTax?.numeric ?? 0;

    const items = cartItems
      .filter((item) => item.quantity > 0)
      .map((item) => {
        return {
          item_id: item.variantCode,
          item_brand: '',
          item_name: item.variantTitle,
          item_category: item.category,
          item_category2: '',
          item_category3: '',
          price: item?.costs?.unitPrice?.exTax?.numeric ?? 0,
          quantity: item.quantity,
          ...(isNewUser !== undefined ? { new_user: isNewUser } : {})
        };
      });

    const gtmPurchaseEvent: Types.GtmPurchaseEvent = {
      event: Types.GTMEventTypes.PURCHASE,
      ecommerce: {
        transaction_id: orderId,
        value: subTotal,
        tax: tax,
        shipping: shipment,
        currency: 'EUR',
        items,
        ...(email ? { email } : {})
      }
    };

    return {
      meta: {
        queue: true,
        preventDispatch: true
      },
      payload: {
        ...gtmPurchaseEvent
      }
    };
  }
);

export const gmtViewCartAction = createAction(
  `gmt/${Types.GTMEventTypes.VIEW_CART}`,
  function prepare({ cartItems }: { cartItems: CartTypes.Product[] }) {
    const subTotal = cartItems?.reduce((accumulator: number, current) => {
      return accumulator + (current?.costs?.lineAmount?.exTax?.numeric ?? 0);
    }, 0);

    const items = cartItems.map((item) => {
      return {
        item_id: item.variantCode,
        item_brand: '',
        item_name: item.variantTitle,
        item_category: item.category,
        item_category2: '',
        item_category3: '',
        price: item?.costs?.unitPrice?.exTax?.numeric ?? 0,
        quantity: item.quantity
      };
    });

    const gtmViewCartEvent: Types.GtmViewCartEvent = {
      event: Types.GTMEventTypes.VIEW_CART,
      ecommerce: {
        value: subTotal,
        currency: 'EUR',
        items
      }
    };

    return {
      payload: {
        ...gtmViewCartEvent
      }
    };
  }
);

export const gmtAddToCartAction = createAction(
  `gmt/${Types.GTMEventTypes.ADD_TO_CART}`,
  function prepare({ cartItems }: { cartItems: CartTypes.Product[] }) {
    const subTotal = cartItems?.reduce((accumulator: number, current) => {
      return accumulator + (current?.costs?.lineAmount?.exTax?.numeric ?? 0);
    }, 0);

    const items = cartItems.map((item) => {
      return {
        item_id: item.variantCode,
        item_brand: '',
        item_name: item.variantTitle,
        item_category: item.category,
        item_category2: '',
        item_category3: '',
        price: item?.costs?.unitPrice?.exTax?.numeric ?? 0,
        quantity: item.quantity
      };
    });

    const gtmAddToCartEvent: Types.GtmAddToCartEvent = {
      event: Types.GTMEventTypes.ADD_TO_CART,
      ecommerce: {
        value: subTotal,
        currency: 'EUR',
        items
      }
    };

    return {
      payload: {
        ...gtmAddToCartEvent
      }
    };
  }
);

// Helper action for when adding from product tiles (not actual cart items)
export const gmtAddToCartFromProductAction = createAction(
  `gmt/${Types.GTMEventTypes.ADD_TO_CART}`,
  function prepare({
    item_id,
    item_name,
    item_category,
    price,
    quantity
  }: {
    item_id: string;
    item_name: string;
    item_category: string;
    price: number;
    quantity: number;
  }) {
    const items = [
      {
        item_id,
        item_brand: '',
        item_name,
        item_category,
        item_category2: '',
        item_category3: '',
        price,
        quantity
      }
    ];

    const gtmAddToCartEvent: Types.GtmAddToCartEvent = {
      event: Types.GTMEventTypes.ADD_TO_CART,
      ecommerce: {
        value: price * quantity,
        currency: 'EUR',
        items
      }
    };

    return {
      payload: {
        ...gtmAddToCartEvent
      }
    };
  }
);

export const gtmRemoveFromCartAction = createAction(
  `gmt/${Types.GTMEventTypes.REMOVE_FROM_CART}`,
  function prepare({ cartItems }: { cartItems: CartTypes.Product[] }) {
    const subTotal = cartItems?.reduce((accumulator: number, current) => {
      return accumulator + (current?.costs?.lineAmount?.exTax?.numeric ?? 0);
    }, 0);

    const items = cartItems.map((item) => {
      return {
        item_id: item.variantCode,
        item_brand: '',
        item_name: item.variantTitle,
        item_category: item.category,
        item_category2: '',
        item_category3: '',
        price: item?.costs?.unitPrice?.exTax?.numeric ?? 0,
        quantity: item.quantity
      };
    });

    const gtmRemoveFromCartEvent: Types.GtmRemoveFromCartEvent = {
      event: Types.GTMEventTypes.REMOVE_FROM_CART,
      ecommerce: {
        value: subTotal,
        currency: 'EUR',
        items
      }
    };

    return {
      payload: {
        ...gtmRemoveFromCartEvent
      }
    };
  }
);

export const gmtBeginCheckoutAction = createAction(
  `gmt/${Types.GTMEventTypes.BEGIN_CHECKOUT}`,
  function prepare({ cartItems }: { cartItems: CartTypes.Product[] }) {
    const subTotal = cartItems?.reduce((accumulator: number, current) => {
      return accumulator + (current?.costs?.lineAmount?.exTax?.numeric ?? 0);
    }, 0);

    const items = cartItems.map((item) => {
      return {
        item_id: item.variantCode,
        item_brand: '',
        item_name: item.variantTitle,
        item_category: item.category,
        item_category2: '',
        item_category3: '',
        price: item?.costs?.unitPrice?.exTax?.numeric ?? 0,
        quantity: item.quantity
      };
    });

    const gtmBeginCheckoutEvent: Types.GtmViewCartEvent = {
      event: Types.GTMEventTypes.BEGIN_CHECKOUT,
      ecommerce: {
        value: subTotal,
        currency: 'EUR',
        items
      }
    };

    return {
      payload: {
        ...gtmBeginCheckoutEvent
      }
    };
  }
);

export const gmtEnhancedConversionAction = createAction(`gmt/${Types.GTMEventTypes.ENHANCED_CONVERSION}`, function prepare(email: string) {
  const gtmEnhancedConversionEvent: Types.GtmEnhancedConversionEvent = {
    event: Types.GTMEventTypes.ENHANCED_CONVERSION,
    email: email
  };

  return {
    payload: {
      ...gtmEnhancedConversionEvent
    }
  };
});

export const gtmAddShippingInfoAction = createAction(
  `gmt/${Types.GTMEventTypes.ADD_SHIPPING_INFO}`,
  function prepare({ currency = 'EUR', shippingTier = 'Ground', value, cartItems }: IAddShippingInfoEvent) {
    const items = cartItems.map((item) => {
      return {
        item_id: item.variantCode,
        item_brand: '',
        item_name: item.variantTitle,
        item_category: item.category,
        item_category2: '',
        item_category3: '',
        price: item?.costs?.unitPrice?.exTax?.numeric ?? 0,
        quantity: item.quantity
      };
    });

    const gtmAddShippingInfoEvent: Types.GtmAddShippingInfoEvent = {
      event: Types.GTMEventTypes.ADD_SHIPPING_INFO,
      ecommerce: {
        currency,
        shipping_tier: shippingTier,
        value,
        items
      }
    };

    return {
      payload: {
        ...gtmAddShippingInfoEvent
      }
    };
  }
);

export const gtmAddPaymentInfoAction = createAction(
  `gmt/${Types.GTMEventTypes.ADD_PAYMENT_INFO}`,
  function prepare({ currency = 'EUR', paymentType, value, cartItems }: IAddPaymentInfoEvent) {
    const items = cartItems.map((item) => {
      return {
        item_id: item.variantCode,
        item_brand: '',
        item_name: item.variantTitle,
        item_category: item.category,
        item_category2: '',
        item_category3: '',
        price: item?.costs?.unitPrice?.exTax?.numeric ?? 0,
        quantity: item.quantity
      };
    });

    const gtmAddPaymentInfoEvent: Types.GtmAddPaymentInfoEvent = {
      event: Types.GTMEventTypes.ADD_PAYMENT_INFO,
      ecommerce: {
        currency,
        payment_type: paymentType,
        value,
        items
      }
    };

    return {
      payload: {
        ...gtmAddPaymentInfoEvent
      }
    };
  }
);

export const gtmViewItemAction = createAction(
  `gmt/${Types.GTMEventTypes.VIEW_ITEM}`,
  function prepare({ currency = 'EUR', value, cartItem }: IViewItemEvent) {
    const item = {
      item_id: cartItem.variantCode,
      item_brand: '',
      item_name: cartItem.variantTitle,
      item_category: cartItem.category,
      item_category2: '',
      item_category3: '',
      price: cartItem?.costs?.unitPrice?.exTax?.numeric ?? 0,
      quantity: cartItem.quantity
    };

    const gtmViewItemEvent: Types.GtmViewItemEvent = {
      event: Types.GTMEventTypes.VIEW_ITEM,
      ecommerce: {
        currency,
        value,
        items: [item]
      }
    };

    return {
      payload: {
        ...gtmViewItemEvent
      }
    };
  }
);

export const gtmViewItemListAction = createAction(
  `gmt/${Types.GTMEventTypes.VIEW_ITEM_LIST}`,
  function prepare({ itemListId, itemListName, cartItems }: IViewItemListEvent) {
    const items = cartItems.map((item) => {
      return {
        item_id: item.variantCode,
        item_brand: '',
        item_name: item.variantTitle,
        item_category: item.category,
        item_category2: '',
        item_category3: '',
        price: item?.costs?.unitPrice?.exTax?.numeric ?? 0,
        quantity: item.quantity
      };
    });

    const gtmViewItemListEvent: Types.GtmViewItemListEvent = {
      event: Types.GTMEventTypes.VIEW_ITEM_LIST,
      ecommerce: {
        item_list_id: itemListId,
        item_list_name: itemListName,
        items
      }
    };

    return {
      payload: {
        ...gtmViewItemListEvent
      }
    };
  }
);
